import { ApiError, StaleError, get, post, put } from '@/api/client';
import { type ApiUserFull, type UserWrite, useRoles, useUserWrites } from '@/api/data';
import { useAuth } from '@/auth/store';
import { T } from '@/i18n';
import { commitGeneration, setPhase, useDirty } from '@/shell/context';
import { useTenant } from '@/shell/tenant';
import { Button, Chip, Panel, toast } from '@/ui';
import { Icon } from '@/ui/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  type Role,
  type RoleFailure,
  isActiveCompany,
  isSelfDemotion,
  lastAdminRefusal,
  roleFailure,
  roleIn,
} from './roles';

/**
 * Bedriftstilgang (TF-PRESERVATION-FINAL B, U03): which of the caller's own
 * companies the person is a member of. Only companies the caller administers
 * are listed; a membership elsewhere is one generic sentence, never a name.
 * The write is a scoped intent, `memberships: { add, remove }`, so whatever is
 * not named is preserved server-side (admin/userController.js updateUser, the
 * lead's change). The password action is a separate drawer and never rides along.
 *
 * Roles (TF-ACCOUNT-ACCESS B): beside each company the person already belongs
 * to, a selector writes the role at once through
 * PUT /admin/users/:id/membership-role (admin/membershipRoleController.js),
 * one request per change, never coupled with the checkbox save. The selector
 * shows the server's value, so a refusal keeps the old role without any undo.
 */
type Membership = { tenant_id: number; role: string };
/** GET /tenants/my-tenants (user/tenantController.js getMyTenants): the caller's companies with the role in each. */
type MyTenants = {
  active_tenant: number;
  tenants: { id: number; name: string }[];
  memberships?: Membership[];
};
export type MembershipsWrite = { memberships: { add: number[]; remove: number[] } };
type Target = ApiUserFull & { userTenantsList?: Membership[] };
/** the role endpoint's answer */
type RoleAnswer = { tenant_id: number; user_id: number; role: Role; changed: boolean; self: boolean };

export function Bedriftstilgang({
  user,
  body,
}: {
  user: Target;
  /** the full record PUT /admin/users/:id demands, with the change merged in (Ansatt fullBody) */
  body: (change: MembershipsWrite) => UserWrite;
}) {
  const { tenant } = useTenant();
  const meUser = useAuth((s) => s.user);
  const setSession = useAuth((s) => s.setSession);
  const me = meUser?.id;
  const qc = useQueryClient();
  const nav = useNavigate();
  const w = useUserWrites();
  // shell/tenant.ts drops `memberships` from the same answer; read it here until useTenant carries it
  const mineQ = useQuery({
    queryKey: ['my-memberships'],
    queryFn: () => get<MyTenants>('/tenants/my-tenants'),
    staleTime: 60_000,
  });
  const administered = (mineQ.data?.tenants ?? []).filter((t) =>
    (mineQ.data?.memberships ?? []).some((m) => Number(m.tenant_id) === t.id && m.role === 'admin'),
  );
  const current = new Set((user.userTenantsList ?? []).map((m) => Number(m.tenant_id)));
  const editable = new Set(administered.map((t) => t.id));
  const elsewhere = [...current].some((id) => !editable.has(id));

  // null mirrors the server; a Set is the unsaved selection
  const [draft, setDraft] = useState<Set<number> | null>(null);
  const [st, setSt] = useState<{ busy: boolean; msg: string | null; unknown: boolean }>({
    busy: false,
    msg: null,
    unknown: false,
  });
  const sel = draft ?? current;
  const add = administered.filter((t) => sel.has(t.id) && !current.has(t.id));
  const remove = administered.filter((t) => !sel.has(t.id) && current.has(t.id));
  const dirty = add.length + remove.length > 0;
  useDirty(`memberships-${user.id}`, dirty, T('Bedriftstilgang'));

  const toggle = (id: number) => {
    const next = new Set(sel);
    next.has(id) ? next.delete(id) : next.add(id);
    setDraft(next);
    setSt({ busy: false, msg: null, unknown: false });
  };
  const cancel = () => {
    setDraft(null);
    setSt({ busy: false, msg: null, unknown: false });
  };
  const save = async () => {
    if (!dirty || st.busy) return;
    setSt({ busy: true, msg: null, unknown: false });
    const leavesHere = tenant != null && remove.some((t) => t.id === tenant.id);
    try {
      await w.update.mutateAsync({
        id: user.id,
        body: body({ memberships: { add: add.map((t) => t.id), remove: remove.map((t) => t.id) } }),
      });
      // useUserWrites re-reads GET /admin/users/:id; the checkboxes follow that answer
      setDraft(null);
      setSt({ busy: false, msg: null, unknown: false });
      toast(T('Bedriftstilgang lagret'));
      if (user.id === me) {
        // my own companies changed: the caches that describe them are re-read
        await Promise.all([
          qc.invalidateQueries({ queryKey: ['my-memberships'] }),
          qc.invalidateQueries({ queryKey: ['my-tenants'] }),
        ]);
        // I have just left this company: the existing access-lost handling takes over, no stale shell
        if (leavesHere) setPhase({ kind: 'access-lost' });
      } else if (leavesHere) {
        nav('/ansatte'); // no longer a member here: this card would answer 404
      }
    } catch (e) {
      // an ApiError is the server's own refusal; anything else (network, a discarded answer) is no answer
      const refused = e instanceof ApiError && !(e instanceof StaleError);
      const lastAdmin = lastAdminRefusal(e);
      if (lastAdmin) {
        // admin/userController.js updateUser: the removal would leave that company without an administrator
        const name = administered.find((t) => t.id === lastAdmin.tenant_id)?.name ?? T('bedriften');
        setSt({
          busy: false,
          unknown: false,
          msg: T('{company} ville stå uten administrator. Gjør noen andre til administrator først.', {
            company: name,
          }),
        });
      } else if (refused) {
        setSt({
          busy: false,
          unknown: false,
          msg: `${T('Kunne ikke lagre bedriftstilgang')}: ${(e as Error).message}`,
        });
      } else {
        // the selection is dropped, never resent; the re-read above shows what holds
        setDraft(null);
        setSt({
          busy: false,
          unknown: true,
          msg: T('Resultatet er ukjent. Tilgangen kan være endret. Ingenting ble sendt på nytt.'),
        });
      }
    }
  };

  // roles: one write in flight at a time, the selector shows the server's value meanwhile
  const [rolePending, setRolePending] = useState<number | null>(null);
  const [roleMsg, setRoleMsg] = useState<RoleFailure | null>(null);
  const [confirm, setConfirm] = useState<{ id: number; name: string } | null>(null);
  const rereadTarget = () => qc.invalidateQueries({ queryKey: ['user', user.id] });
  /**
   * I have just taken my own administrator role away in the active company: the
   * token still says admin, so the session is re-issued the way a company switch
   * does it (shell/switch.ts proceed), then the caches and the route start over.
   */
  const refreshSession = async (t: { id: number; name: string }) => {
    let r: { token?: unknown; role?: unknown; active_tenant?: unknown } | null = null;
    try {
      r = await post('/tenants/switch-tenant', { tenant_id: t.id });
    } catch {
      r = null;
    }
    const token = r && typeof r.token === 'string' ? r.token : null;
    const role = r && typeof r.role === 'string' ? r.role : null;
    if (!token || !role || !meUser || Number(r?.active_tenant) !== t.id) {
      setRoleMsg({
        msg: T(
          'Rollen er endret, men økten kunne ikke oppdateres. Logg inn på nytt for å se den nye tilgangen.',
        ),
        reread: false,
        unknown: true,
      });
      return;
    }
    setSession(token, { ...meUser, role });
    commitGeneration(meUser.id, t.id, t.name);
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['my-memberships'] }),
      qc.invalidateQueries({ queryKey: ['my-tenants'] }),
    ]);
    nav('/');
  };
  const writeRole = async (t: { id: number; name: string }, role: Role) => {
    if (rolePending != null) return;
    setConfirm(null);
    setRoleMsg(null);
    setRolePending(t.id);
    try {
      const r = await put<RoleAnswer>(`/admin/users/${user.id}/membership-role`, { tenant_id: t.id, role });
      await rereadTarget(); // the selector follows GET /admin/users/:id, not the request
      toast(T('Rolle lagret'));
      if (r.self && role === 'user' && isActiveCompany(t.id, tenant?.id)) {
        await refreshSession(t);
      } else if (r.self) {
        await Promise.all([
          qc.invalidateQueries({ queryKey: ['my-memberships'] }),
          qc.invalidateQueries({ queryKey: ['my-tenants'] }),
        ]);
      }
    } catch (e) {
      const f = roleFailure(e);
      setRoleMsg(f);
      if (f.reread) await rereadTarget();
    } finally {
      setRolePending(null);
    }
  };
  const changeRole = (t: { id: number; name: string }, role: Role) => {
    if (isSelfDemotion(user.id, me, role))
      setConfirm(t); // asked first, written by the button below
    else void writeRole(t, role);
  };

  return (
    <Panel title={T('Bedriftstilgang')} sub={T('bedriftene du administrerer')}>
      {mineQ.isError ? (
        <p className="hint">{T('Kunne ikke hente bedriftene dine.')}</p>
      ) : (
        <div className="btlist" data-testid="memberships">
          {administered.map((t) => {
            const on = sel.has(t.id);
            const role = roleIn(user.userTenantsList, t.id); // null: not a member yet, no selector
            return (
              <div key={t.id} className="btrow">
                <label className={on ? '' : 'off'}>
                  <input type="checkbox" checked={on} disabled={st.busy} onChange={() => toggle(t.id)} />
                  {t.name}
                  {add.some((a) => a.id === t.id) && (
                    <Chip tone="ok" small>
                      {T('Legges til')}
                    </Chip>
                  )}
                  {remove.some((r) => r.id === t.id) && (
                    <Chip tone="warn" small>
                      {T('Fjernes')}
                    </Chip>
                  )}
                </label>
                {role != null && (
                  <RolePicker
                    tenant={t}
                    role={role}
                    busy={rolePending != null || st.busy}
                    onChange={(next) => changeRole(t, next)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
      {elsewhere && (
        <p className="hint" data-testid="memberships-elsewhere">
          {T('Tilgang til andre bedrifter endres ikke her.')}
        </p>
      )}
      {confirm && (
        <div className="al warn" role="alert" data-testid="role-confirm">
          <span>!</span>
          <span>
            {T('Du mister administratorkontrollene i {company}.', { company: confirm.name })}
            {isActiveCompany(confirm.id, tenant?.id) &&
              ` ${T('Dette er den aktive bedriften, så menyen endres.')}`}
          </span>
          <span className="sp" />
          <Button small onClick={() => setConfirm(null)} disabled={rolePending != null}>
            {T('Avbryt')}
          </Button>
          <Button
            small
            tone="dgr"
            onClick={() => void writeRole(confirm, 'user')}
            disabled={rolePending != null}
          >
            {T('Gjør meg til ansatt')}
          </Button>
        </div>
      )}
      {roleMsg && (
        <div className={`al ${roleMsg.unknown ? 'warn' : 'dgr'}`} role="alert" data-testid="role-msg">
          <span>{roleMsg.unknown ? '?' : <Icon name="stop" />}</span>
          <span>{roleMsg.msg}</span>
        </div>
      )}
      {st.msg && (
        <div className={`al ${st.unknown ? 'warn' : 'dgr'}`} role="alert">
          <span>{st.unknown ? '?' : <Icon name="stop" />}</span>
          <span>{st.msg}</span>
        </div>
      )}
      {dirty && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <span className="sp" />
          <Button onClick={cancel} disabled={st.busy}>
            {T('Avbryt')}
          </Button>
          <Button tone="pri" onClick={save} disabled={st.busy}>
            {st.busy ? T('Lagrer…') : T('Lagre')}
          </Button>
        </div>
      )}
    </Panel>
  );
}

/**
 * The roles of one company, read from that company rather than from a list in the client:
 * a person can be in two companies that have named their roles differently, and the server
 * refuses a key the company has not defined (custom roles, 2026-09-16).
 */
function RolePicker({
  tenant,
  role,
  busy,
  onChange,
}: {
  tenant: { id: number; name: string };
  role: string;
  busy: boolean;
  onChange: (next: string) => void;
}) {
  const q = useRoles(tenant.id);
  const roles = q.data?.roles ?? [];
  // the role held is always offered, even before the list arrives, so the select never
  // silently shows somebody as something they are not
  const options = roles.length
    ? roles
    : [{ key: role, name: role, permissions: [], is_system: false, revision: 0, members: 0 }];
  const chosen = roles.find((r) => r.key === role);
  const names = (q.data?.permissions ?? [])
    .filter((p) => (chosen?.permissions ?? []).includes(p.key))
    .map((p) => p.name);
  return (
    <>
      <select
        className="inp"
        aria-label={T('Rolle i {company}', { company: tenant.name })}
        value={role}
        disabled={busy || q.isLoading}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.key} value={o.key}>
            {o.name}
          </option>
        ))}
      </select>
      {/* what the chosen role actually lets the person do, from the company's own definition */}
      <p className="hint" data-testid="role-hint">
        {chosen?.key === 'admin'
          ? T('Alt i bedriften, inkludert roller, tilganger og konto.')
          : names.length
            ? names.join(', ')
            : T('Egne vakter, timer og søknader.')}
      </p>
    </>
  );
}
